import { useSignal } from "@preact/signals-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import { useEffect, useState } from "react";
import PageForm from "../../templates/PageForm";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import CurrencyInput from "../../molecules/CurrencyInput";
import DayPicker from "../../molecules/DayPicker";
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage } from '../../../utils/formErrorUtils';
import { FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaTimes, FaSearch } from "react-icons/fa";
import BadgeRegistrationStatus from "../../atoms/BadgeRegistrationStatus";
import ReceivablesDownloader from "./ReceivablesDownloader";
import useProvinces from "../../../hooks/useProvinces";
import useCities from "../../../hooks/useCities";


const CONTRACTS_API_URL = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_URL;
const CONTRACTS_API_KEY = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_KEY;
const BACKOFFICE_URL = process.env.REACT_APP_BACKOFFICE_URL;
const PERSON_SERVICE_URL = process.env.REACT_APP_PERSON_API_URL;
const OPERATIONS_LIST_URL = '/contracts/0/100/id/ASC/s=;';
const API_KEY_PERSON = process.env.REACT_APP_API_KEY;

interface DuplicateItem {
    cfop: string;
    issue_date: string;
    debtor_name: string;
    document_id: string;
    address_city: string;
    debtor_email: string;
    address_phone: string;
    address_state: string;
    assignor_cnpj: string;
    assignor_name: string;
    address_number: string;
    address_street: string;
    document_title: string;
    xml_size_bytes: number;
    address_country: string;
    debtor_cpf_cnpj: string;
    address_district: string;
    address_zip_code: string;
    receivable_v_pag: string;
    address_ibge_code: string;
    receivable_amount: number;
    receivable_nat_op: string;
    receivable_number: string;
    address_complement?: string;
    receivable_ind_pag: string;
    receivable_due_date: string;
    disbursement_value: number;
    fee_name: string;
    fee_value: number;
    guarantee_percent: number;
    main_risk: string;
    external_link: string;
}

interface DisbursementItem {
    amount: number;
    date: string;
}

interface InstallmentItem {
    number: number;
    value: number;
    dueDate: string;
    format: string;
}

interface GuarantorItem {
    uuid: string;
    name: string;
    entity_type: string;
}

interface FeeItem {
    name: string;
    rate: number;
    amount: number;
    form: string;
}

interface ArtifactItem {
    list: string;
    files: string[];
}

interface SignedContractFile {
    file_id: string;
    file_name: string;
}

interface AcknowledgementDebtForm {
    assignor: string;
    recipient: string;
    total_amount: number;
    expiration_date: string;
    emission_date: string;
    operation_number: string;
    external_link: string;
    rebate_amount: number;
    guarantee_value: number | null;
    status?: string;
    sequencial_number?: number;
    originator: string;
    operation_uf: string;
    operation_city: string;
    original_contract_discharged: number;
    crop_type: {
        name: string;
        id: string;
    };
    duplicates: {
        quantity: number;
        total: number;
        items: DuplicateItem[];
    };
    disbursement: {
        totalAmount: number;
        disbursements: DisbursementItem[];
    };
    installments: InstallmentItem[];
    installment_format: string;
    guarantors: GuarantorItem[];
    main_risk: {
        uuid: string;
        name: string;
    };
    fees: FeeItem[];
    artifacts: ArtifactItem[];
    signed_contract_file: SignedContractFile;
}

const AcknowledgementDebtPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const operationType = searchParams.get('operation_type') || 'ACKNOWLEDGEMENT_OF_DEBT';
    const credit_operation_id = params.credit_operation_id || params.id;
    const artifactFiles = useSignal<File[]>([]);
    const contractFiles = useSignal<File[]>([]);
    const [existingContractFile, setExistingContractFile] = useState<string>('');
    const [existingArtifactFiles, setExistingArtifactFiles] = useState<string[]>([]);
    const [removedContractFile, setRemovedContractFile] = useState<boolean>(false);
    const [removedArtifactFiles, setRemovedArtifactFiles] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;
    const [selectedAssignor, setSelectedAssignor] = useState('');
    const [selectedBeneficiary, setSelectedBeneficiary] = useState('');
    const { fetchProvinces } = useProvinces();
    const { fetchCities } = useCities();
    const [provinces, setProvinces] = useState<Array<{ value: string, label: string, initials: string }>>([]);
    const [cities, setCities] = useState<Array<{ value: string, label: string }>>([]);

    const form = useSignal<AcknowledgementDebtForm>({
        assignor: '',
        recipient: '',
        total_amount: 0,
        expiration_date: '',
        emission_date: '',
        operation_number: '',
        external_link: '',
        rebate_amount: 0,
        guarantee_value: null,
        status: '',
        sequencial_number: 0,
        originator: '',
        operation_uf: '',
        operation_city: '',
        original_contract_discharged: 0,
        crop_type: {
            name: '',
            id: ''
        },
        duplicates: {
            quantity: 0,
            total: 0,
            items: []
        },
        disbursement: {
            totalAmount: 0,
            disbursements: []
        },
        installments: [],
        installment_format: 'VALOR',
        guarantors: [],
        main_risk: {
            uuid: '',
            name: ''
        },
        fees: [],
        artifacts: [],
        signed_contract_file: {
            file_id: '',
            file_name: ''
        }
    });

    const isLoading = useSignal(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const [assignors, setAssignors] = useState<Array<{ value: string; label: string }>>([]);
    const [recipients, setRecipients] = useState<Array<{ value: string; label: string }>>([]);
    const [searchResults, setSearchResults] = useState<Array<{ value: string, label: string, personData?: any }>>([]);
    const [searchDestinatario, setSearchDestinatario] = useState('');
    const [selectedDestinatario, setSelectedDestinatario] = useState('');
    const [recipientData, setRecipientData] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalSearchValue, setModalSearchValue] = useState('');
    const [modalSearchResults, setModalSearchResults] = useState<Array<{ value: string, label: string, personData?: any }>>([]);
    const newDisbursement = useSignal<{ amount: number; date: string | null }>({ amount: 0, date: new Date().toLocaleDateString('pt-BR') });

    const newInstallment = useSignal<{ number: number; value: number; dueDate: string | null; format: string }>({
        number: 1,
        value: 0,
        dueDate: new Date().toLocaleDateString('pt-BR'),
        format: 'VALOR'

    });
    const newGuarantor = useSignal<{ uuid: string; name: string; entity_type: string }>({
        uuid: '',
        name: '',
        entity_type: ''
    });
    const [guarantors, setGuarantors] = useState<Array<{ value: string; label: string; entity_type: string }>>([]);
    const [guarantorSearch, setGuarantorSearch] = useState('');
    const [cropTypes, setCropTypes] = useState<Array<{ value: string; label: string }>>([]);
    const [assignorParticipant, setAssignorParticipant] = useState<{ uuid: string; name: string }>({ uuid: '', name: '' });
    const newFee = useSignal<{ name: string; rate: number; amount: number; form: string }>({
        name: '',
        rate: 0,
        amount: 0,
        form: ''
    });

    const filteredGuarantors = guarantors.filter(guarantor =>
        guarantor.label.toLowerCase().includes(guarantorSearch.toLowerCase())
    );

    const handlePersonSearch = async (searchValue: string) => {
        setSearchDestinatario(searchValue);
        if (searchValue.length >= 2) {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());
                const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${searchValue}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY_PERSON || '',
                        'Content-Type': 'application/json'
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    const personItems = data.results.map((item: any) => {
                        const cnpjDoc = item.documents?.find((doc: any) => doc.type === 'CNPJ');
                        const cpfDoc = item.documents?.find((doc: any) => doc.type === 'CPF');
                        const document = cnpjDoc?.number || cpfDoc?.number || '';
                        return {
                            value: item.uuid,
                            label: `${document} - ${item.name}`,
                            personData: item
                        };
                    });
                    setSearchResults(personItems);
                    form.value = { ...form.value, recipient: personItems[0]?.personData.name };
                }
            } catch (error) {
                console.error('Erro ao buscar pessoa:', error);
            }
        } else {
            setSearchResults([]);
        }
    };

    const handleSelectParticipant = (selected: any) => {
        setSelectedDestinatario(selected.value);
        const [, name] = selected.label.split(' - ');
        form.value = { ...form.value, recipient: name || '' };

        if (selected.personData) {
            const cnpjDoc = selected.personData.documents?.find((doc: any) => doc.type === 'CNPJ');
            const cpfDoc = selected.personData.documents?.find((doc: any) => doc.type === 'CPF');
            const document = cnpjDoc?.number || cpfDoc?.number || '';
            setSearchDestinatario(document);

            setRecipientData({
                ...recipientData,
                id: recipientData != null && selected.personData.id !== recipientData.id ? null : recipientData?.id,
                name: selected.personData.name,
                document: document,
                person_id: selected.personData.uuid,
                is_active: true,
                is_primary: false,
                effective_from: new Date().toISOString()
            });
        }
        setSearchResults([]);
    };

    const handleModalSearch = async (searchValue: string) => {
        setModalSearchValue(searchValue);
        if (searchValue.length >= 2) {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());
                const response = await fetch(`${BACKOFFICE_URL}/services/person/ALL/${searchValue}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': CONTRACTS_API_KEY || ''
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    const personItems = data.map((item: any) => {
                        const cnpjDoc = item.documents?.find((doc: any) => doc.type === 'CNPJ');
                        const cpfDoc = item.documents?.find((doc: any) => doc.type === 'CPF');
                        const document = cnpjDoc?.number || cpfDoc?.number || '';
                        return {
                            value: item.uuid,
                            label: `${document} - ${item.name}`,
                            personData: item
                        };
                    });
                    setModalSearchResults(personItems);
                }
            } catch (error) {
                console.error('Erro ao buscar pessoa:', error);
            }
        } else {
            setModalSearchResults([]);
        }
    };

    const handleModalSelectParticipant = (selected: any) => {
        handleSelectParticipant(selected);
        setIsModalOpen(false);
        setModalSearchValue('');
        setModalSearchResults([]);
    };

    const fetchContractData = async () => {
        if (!credit_operation_id) {
            console.log('No credit_operation_id, skipping fetch');
            return;
        }
        if (!CONTRACTS_API_URL || !CONTRACTS_API_KEY) {
            console.log('Missing CONTRACTS_API_URL or CONTRACTS_API_KEY');
            return;
        }

        try {
            isLoading.value = true;
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());
            const response = await fetch(`${CONTRACTS_API_URL}/contracts/receivables_assignment/${credit_operation_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': CONTRACTS_API_KEY || ''
                }
            });

            if (response.ok) {
                const data = await response.json();
                const schema = data.operation_dictionary || {};

                const assignorPart = data.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR');
                const recipientParticipant = data.participants?.find((p: any) => p.participant_type_name === 'RECIPIENT');

                if (assignorPart) {
                    setAssignorParticipant({
                        uuid: assignorPart.person_id || '00000000-0000-0000-0000-000000000000',
                        name: assignorPart.name || ''
                    });
                }

                form.value = {
                    ...form.value,
                    operation_number: data.operation_number || '',
                    status: data.status || '',
                    sequencial_number: data.sequencial_number || 0,
                    assignor: assignorPart?.name || data.assignor || '',
                    recipient: recipientParticipant?.name || data.recipient || '',
                    originator: schema.originator || '',
                    operation_uf: schema.operation_uf || 0,
                    operation_city: schema.operation_city || '',
                    original_contract_discharged: schema.original_contract_discharged || 0,
                    total_amount: schema.total_amount || data.nominal_value || 0,
                    expiration_date: schema.expiration_date || '',
                    emission_date: schema.emission_date || '',
                    external_link: schema.external_link || '',
                    rebate_amount: schema.rebate_amount || 0,
                    crop_type: schema.crop_type || { name: '', id: '' },
                    duplicates: schema.duplicates || { quantity: 0, total: 0, items: [] },
                    disbursement: {
                        totalAmount: schema.disbursement?.totalAmount || 0,
                        disbursements: schema.disbursement?.disbursements?.map((d: any) => ({
                            amount: d.amount || 0,
                            date: d.date ? new Date(d.date).toLocaleDateString('pt-BR') : ''
                        })) || []
                    },
                    installments: reprocessInstallmentNumbers(
                        schema.installment?.items?.map((inst: any, index: number) => ({
                            number: index + 1,
                            value: inst.value || inst.amount || 0,

                            dueDate: inst.dueDate ? new Date(inst.dueDate).toLocaleDateString('pt-BR') : '',
                            format: inst.format || 'VALOR'

                        })) ||
                        schema.installments?.map((inst: any) => ({
                            number: inst.number || 0,
                            value: inst.value || inst.amount || 0,
                            dueDate: inst.dueDate ? new Date(inst.dueDate).toLocaleDateString('pt-BR') : '',
                            format: inst.format || 'VALOR'
                        })) || []
                    ),
                    guarantee_value: schema.guarantee_value || null,
                    guarantors: schema.guarantors || [],
                    main_risk: schema.main_risk || { uuid: '', name: '' },
                    fees: schema.fees || [],
                    artifacts: schema.artifacts || [],
                    signed_contract_file: schema.signed_contract_file || { file_id: '', file_name: '' }
                };

                // Load existing files from operation_dictionary
                if (schema.files) {
                    setExistingContractFile(schema.files.contract_file || '');
                    setExistingArtifactFiles(schema.files.artefacts || []);
                }

            } else {
                console.error('Response not ok:', response.status, response.statusText);
                toast.error('Erro ao carregar dados da operação');
            }
        } catch (error) {
            console.error('Erro ao buscar operação:', error);
            toast.error('Erro ao conectar com o servidor');
        } finally {
            isLoading.value = false;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${BACKOFFICE_URL}/services/person/ALL`);
                if (response.ok) {
                    const data = await response.json();
                    console.log('Fetched persons data:', data);
                    const items = data.map((item: any) => ({
                        value: item.uuid,
                        label: `${item.name} - ${item.documents?.find((doc: any) => doc.type === 'CNPJ')?.number || ''}`
                    }));

                    setAssignors(items);
                    setRecipients(items);
                    const guarantorItems = data.map((item: any) => ({
                        value: item.uuid,
                        label: `${item.name} - ${item.documents?.find((doc: any) => doc.type === 'CNPJ' || doc.type === 'CPF')?.number || ''}`,
                        entity_type: item.personType === 'LEGAL_PERSON' ? 'PJ' : 'PF'
                    }));
                    setGuarantors(guarantorItems);
                }
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }
        };

        const fetchCropTypes = async () => {
            try {
                const response = await fetch(`${BACKOFFICE_URL}/services/croptypes/all`);
                if (response.ok) {
                    const data = await response.json();
                    setCropTypes(data.map((crop: any) => ({
                        value: crop.id.toString(),
                        label: crop.name
                    })));
                }
            } catch (error) {
                console.error('Erro ao buscar crop types:', error);
            }
        };

        const fetchProvincesData = async () => {
            try {
                const provincesData = await fetchProvinces();
                const provinceItems = provincesData.map((item: any) => ({
                    value: item.initials,
                    label: item.name,
                    initials: item.initials
                }));
                setProvinces(provinceItems);
                return provinceItems;
            } catch (error) {
                console.error('Erro ao buscar províncias:', error);
                return [];
            }
        };

        const loadDataSequentially = async () => {
            const provincesData = await fetchProvincesData();

            if (credit_operation_id) {
                await fetchContractData();
                // Após carregar os dados do contrato, buscar cidades se UF estiver selecionada
                if (form.value.operation_uf) {
                    const selectedProvince = provincesData.find((p: { value: string; label: string; initials: string }) => p.value === form.value.operation_uf);

                    if (selectedProvince) {
                        fetchCitiesWrapper(selectedProvince.initials);
                    }
                }
            }
        };

        fetchData();
        fetchCropTypes();
        loadDataSequentially();
    }, [credit_operation_id]);

    const handleProvinceChange = async (initials: string) => {
        fetchCitiesWrapper(initials);
        if (initials) {
            const selectedProvince = provinces.find(p => p.initials === initials);
            if (selectedProvince) {

            }
            form.value = {
                ...form.value,
                operation_uf: initials,
                operation_city: ''
            };
        } else {
            setCities([]);
            form.value = {
                ...form.value,
                operation_uf: '',
                operation_city: ''
            };
        }
    };

    const fetchCitiesWrapper = async (initials: string) => {
        try {
            if (initials) {

                const citiesData = await fetchCities(initials);
                const cityItems = citiesData.map((item: any) => ({
                    value: item.name,
                    label: item.name
                }));
                setCities(cityItems);
            }
        } catch (error) {
            console.error('Erro ao buscar cidades:', error);
        }
    };



    const addDisbursement = () => {
        if (form.value.disbursement.disbursements.length >= 2) {
            toast.error("Máximo de 2 desembolsos permitidos");
            return;
        }
        if (newDisbursement.value.amount <= 0) {
            toast.error("Informe um valor maior que zero");
            return;
        }

        const currentDisbursements = [...form.value.disbursement.disbursements];
        const newItem = { amount: newDisbursement.value.amount, date: newDisbursement.value.date || new Date().toLocaleDateString('pt-BR') };
        currentDisbursements.push(newItem);
        const totalAmount = currentDisbursements.reduce((sum, d) => sum + d.amount, 0);

        form.value = {
            ...form.value,
            disbursement: {
                disbursements: currentDisbursements,
                totalAmount
            }
        };
        newDisbursement.value = { amount: 0, date: new Date().toLocaleDateString('pt-BR') };
    };

    const reprocessInstallmentNumbers = (installments: InstallmentItem[]) => {
        const sortedInstallments = [...installments].sort((a, b) => {
            const dateA = new Date(a.dueDate.split('/').reverse().join('-'));
            const dateB = new Date(b.dueDate.split('/').reverse().join('-'));
            return dateA.getTime() - dateB.getTime();
        });

        return sortedInstallments.map((installment, index) => ({
            ...installment,
            number: index + 1
        }));
    };

    const updateTotalAndExpirationFromInstallments = (installments: InstallmentItem[]) => {
        const hasPercentualInstallments = installments.some(inst => inst.format === 'PERCENTUAL');
        const valorInstallments = installments.filter(inst => inst.format === 'VALOR');

        const totalAmount = hasPercentualInstallments ? form.value.total_amount : valorInstallments.reduce((sum, inst) => sum + inst.value, 0);
        const expirationDate = installments.length > 0
            ? installments.sort((a, b) => new Date(b.dueDate.split('/').reverse().join('-')).getTime() - new Date(a.dueDate.split('/').reverse().join('-')).getTime())[0].dueDate
            : '';

        form.value = {
            ...form.value,
            total_amount: totalAmount,
            expiration_date: expirationDate
        };
    };

    const addInstallment = () => {
        if (newInstallment.value.value <= 0) {
            toast.error("Informe um valor maior que zero");
            return;
        }
        if (!newInstallment.value.dueDate) {
            toast.error("Informe a data de vencimento");
            return;
        }

        const currentInstallments = [...form.value.installments];
        const newItem = {
            number: 1,
            value: newInstallment.value.value,
            dueDate: newInstallment.value.dueDate,
            format: newInstallment.value.format
        };
        currentInstallments.push(newItem);

        const reprocessedInstallments = reprocessInstallmentNumbers(currentInstallments);

        form.value = {
            ...form.value,
            installments: reprocessedInstallments
        };

        updateTotalAndExpirationFromInstallments(reprocessedInstallments);

        newInstallment.value = {
            number: 1,
            value: 0,
            dueDate: new Date().toLocaleDateString('pt-BR'),
            format: 'VALOR'
        };
    };

    const addGuarantor = () => {
        if (!newGuarantor.value.uuid) {
            toast.error("Selecione um avalista");
            return;
        }

        const selectedGuarantor = guarantors.find(g => g.value === newGuarantor.value.uuid);
        if (!selectedGuarantor) return;

        const currentGuarantors = [...form.value.guarantors];
        const newItem = {
            uuid: newGuarantor.value.uuid,
            name: selectedGuarantor.label.split(' - ')[0],
            entity_type: selectedGuarantor.entity_type
        };
        currentGuarantors.push(newItem);

        form.value = {
            ...form.value,
            guarantors: currentGuarantors
        };

        newGuarantor.value = {
            uuid: '',
            name: '',
            entity_type: ''
        };
    };

    const handleDuplicateChange = (index: number, field: string, value: any) => {
        const items = [...form.value.duplicates.items];
        items[index] = { ...items[index], [field]: value };
        const total = items.reduce((sum, item) => sum + (item.receivable_amount || 0), 0);
        form.value = {
            ...form.value,
            duplicates: { ...form.value.duplicates, items, total }
        };
    };

    const handleJsonDataLoaded = (data: any) => {
        if (data.duplicates?.items) {
            form.value = {
                ...form.value,
                operation_number: data.operation_code || form.value.operation_number,
                duplicates: {
                    quantity: data.duplicates.quantity || data.duplicates.items.length,
                    total: data.duplicates.total || 0,
                    items: data.duplicates.items
                }
            };
        }
    };

    const addFee = () => {
        if (!newFee.value.name) {
            toast.error("Informe o nome da taxa");
            return;
        }
        if (newFee.value.rate < 0) {
            toast.error("Informe uma taxa válida");
            return;
        }
        if (newFee.value.amount < 0) {
            toast.error("Informe um valor válido");
            return;
        }
        if (!newFee.value.form) {
            toast.error("Selecione a forma da taxa");
            return;
        }

        const currentFees = [...form.value.fees];
        const newItem = {
            name: newFee.value.name,
            rate: newFee.value.rate,
            amount: newFee.value.amount,
            form: newFee.value.form
        };
        currentFees.push(newItem);

        form.value = {
            ...form.value,
            fees: currentFees
        };

        newFee.value = {
            name: '',
            rate: 0,
            amount: 0,
            form: ''
        };
    };



    const handleContractFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            form.value = {
                ...form.value,
                signed_contract_file: {
                    file_id: `file_${Date.now()}`,
                    file_name: file.name
                }
            };
        }
    };

    const isFormDisabled = ['Suspenso', 'Cancelado', 'Finalizado com sucesso', 'Expirado'].includes(form.value.status || '');

    const generateParticipants = () => {
        return [
            {
                id: null,
                name: form.value.assignor,
                document: "",
                is_active: true,
                person_id: "00000000-0000-0000-0000-000000000000",
                is_primary: true,
                effective_to: null,
                effective_from: new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: 3,
                participation_order: 1,
                guarantee_percentage: null,
                participant_type_name: "ASSIGNOR",
                receives_disbursement: false
            },
            {
                id: null,
                name: form.value.recipient,
                document: "",
                is_active: true,
                person_id: selectedDestinatario || "00000000-0000-0000-0000-000000000000",
                is_primary: false,
                effective_to: null,
                effective_from: new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: 5,
                participation_order: 2,
                guarantee_percentage: null,
                participant_type_name: "RECIPIENT",
                receives_disbursement: false
            },
            ...form.value.guarantors.map((guarantor, index) => ({
                id: null,
                name: guarantor.name,
                document: "",
                is_active: true,
                person_id: guarantor.uuid,
                is_primary: false,
                effective_to: null,
                effective_from: new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: 6,
                participation_order: index + 3,
                guarantee_percentage: null,
                participant_type_name: "GUARANTOR",
                receives_disbursement: false
            }))
        ];
    };

    const handleSubmit = async () => {
        if (isFormDisabled) {
            toast.error('Não é possível salvar. Status não permite alterações.');
            return;
        }

        if (!CONTRACTS_API_URL || !CONTRACTS_API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        if (!form.value.original_contract_discharged || form.value.original_contract_discharged <= 0) {
            toast.error('Operação quitada deve ser preenchida com um valor maior que zero');
            return;
        }

        // Validar tamanho dos arquivos (máximo 6MB)
        const maxSize = 6 * 1024 * 1024; // 6MB em bytes
        let totalSize = 0;

        contractFiles.value.forEach(file => {
            totalSize += file.size;
        });

        artifactFiles.value.forEach(file => {
            totalSize += file.size;
        });

        if (totalSize > maxSize) {
            toast.error(`O tamanho total dos arquivos (${(totalSize / 1024 / 1024).toFixed(2)}MB) excede o limite de 6MB`);
            return;
        }

        try {
            isLoading.value = true;

            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            // Construir payload conforme JSON schema
            const payload = {
                type: operationType || "ACKNOWLEDGEMENT_OF_DEBT",
                assignor: "00000000-0000-0000-0000-000000000000",
                recipient: selectedDestinatario || "00000000-0000-0000-0000-000000000000",
                credit_operation_id: credit_operation_id,
                operation_number: form.value.operation_number || "",
                operation_type: "ACKNOWLEDGEMENT_OF_DEBT",
                operation_schema_id: 3,
                approved_on: null,
                approved_value: null,
                current_balance: null,
                debt_balance_id: null,
                nominal_value: form.value.total_amount || 0,
                due_date: form.value.expiration_date || null,
                expiration_date: form.value.expiration_date || null,
                participants: generateParticipants(),
                operation_dictionary: {
                    assignor: "00000000-0000-0000-0000-000000000000",
                    recipient: selectedDestinatario || "00000000-0000-0000-0000-000000000000",
                    emission_date: form.value.emission_date ? form.value.emission_date.split('/').reverse().join('-') : "",
                    guarantee_value: form.value.guarantee_value,
                    rebate_amount: form.value.rebate_amount || 0,
                    fees: form.value.fees.map(fee => ({
                        rate: fee.rate || 0,
                        amount: fee.amount || 0,
                        form: fee.form || "",
                        name: fee.name || ""
                    })),
                    disbursement: {
                        disbursements: form.value.disbursement.disbursements.map(d => ({
                            amount: d.amount || 0,
                            date: d.date ? d.date.split('/').reverse().join('-') : ""
                        })),
                        totalAmount: form.value.disbursement.totalAmount || 0
                    },
                    installment: {
                        total: form.value.installments.reduce((sum, inst) => sum + inst.value, 0),
                        lastDueDate: form.value.installments.length > 0
                            ? form.value.installments.sort((a, b) => new Date(b.dueDate.split('/').reverse().join('-')).getTime() - new Date(a.dueDate.split('/').reverse().join('-')).getTime())[0].dueDate.split('/').reverse().join('-')
                            : "",
                        items: form.value.installments.map(inst => ({
                            value: inst.value || 0,
                            dueDate: inst.dueDate ? inst.dueDate.split('/').reverse().join('-') : "",
                            format: inst.format || "VALOR"
                        }))
                    },
                    guarantors: form.value.guarantors.map(guarantor => ({
                        uuid: guarantor.uuid || "",
                        name: guarantor.name || "",
                        entity_type: "FIDEJUSSÓRIA"
                    })),
                    duplicates: {
                        items: form.value.duplicates?.items?.map(item => ({
                            document_id: item.document_id || "",
                            document_title: item.document_title || "",
                            cfop: item.cfop || "",
                            issue_date: item.issue_date || "",
                            xml_size_bytes: item.xml_size_bytes || 0,
                            assignor_name: item.assignor_name || "",
                            assignor_cnpj: item.assignor_cnpj || "",
                            debtor_cpf_cnpj: item.debtor_cpf_cnpj || "",
                            debtor_name: item.debtor_name || "",
                            debtor_email: item.debtor_email || "",
                            address_street: item.address_street || "",
                            address_number: item.address_number || "",
                            address_complement: item.address_complement || "",
                            address_district: item.address_district || "",
                            address_ibge_code: item.address_ibge_code || "",
                            address_city: item.address_city || "",
                            address_state: item.address_state || "",
                            address_zip_code: item.address_zip_code || "",
                            address_country: item.address_country || "",
                            address_phone: item.address_phone || "",
                            receivable_number: item.receivable_number || "",
                            receivable_due_date: item.receivable_due_date || "",
                            receivable_amount: item.receivable_amount || 0,
                            receivable_v_pag: item.receivable_v_pag || "",
                            receivable_nat_op: item.receivable_nat_op || "",
                            receivable_ind_pag: item.receivable_ind_pag || ""
                        })) || [],
                        total: form.value.duplicates?.total || 0,
                        quantity: form.value.duplicates?.quantity || 0
                    },
                    operation_number: form.value.operation_number || "",
                    external_link: form.value.external_link || "",
                    operation_uf: form.value.operation_uf || 0,
                    operation_city: form.value.operation_city || "",
                    original_contract_discharged: form.value.original_contract_discharged || 0,
                    product: form.value.crop_type.name || "",
                    main_risk: {
                        uuid: form.value.main_risk.uuid || "",
                        name: form.value.main_risk.name || ""
                    },
                    credit_product_type: "ACKNOWLEDGEMENT_OF_DEBT",
                    is_settled: false,
                    artifacts: [
                        {
                            list: "DOCUMENTOS",
                            files: artifactFiles.value.map(file => file.name)
                        }
                    ],
                    crop_type: {
                        id: form.value.crop_type.id || "",
                        name: form.value.crop_type.name || ""
                    },
                    originator: form.value.originator || "",
                    files: {
                        contract_file: contractFiles.value.length > 0
                            ? `${form.value.sequencial_number}${contractFiles.value[0].name.substring(contractFiles.value[0].name.lastIndexOf('.'))}`
                            : (removedContractFile ? '' : existingContractFile),
                        artefacts: [
                            ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                            ...artifactFiles.value.map(file => file.name)
                        ]
                    }
                }
            };


            let requestBody;
            let headers: Record<string, string> = {
                'Authorization': `Bearer ${token}`,
                'x-api-key': CONTRACTS_API_KEY || ''
            };

            if (contractFiles.value.length > 0 || artifactFiles.value.length > 0) {
                const formData = new FormData();
                if (contractFiles.value.length > 0) {
                    formData.append('contract_file', contractFiles.value[0]);
                }
                artifactFiles.value.forEach((file, index) => {
                    formData.append('artifacts', file);
                });
                formData.append('data', JSON.stringify(payload));
                requestBody = formData;
            } else {
                headers['Content-Type'] = 'application/json';
                requestBody = JSON.stringify(payload);
            }

            const url = `${CONTRACTS_API_URL}/contracts/receivables_assignment/update`;

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: requestBody
            });

            if (response.ok) {
                const message = credit_operation_id
                    ? "Confissão de dívida atualizado com sucesso!"
                    : "Confissão de dívida criado com sucesso!";
                toast.success(message);
                navigate(-1);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar operação');
                errorFields.value = errorData.validationMessages?.map((item: any) => ({
                    fieldName: item.fieldName,
                    message: item.code
                })) || [];
            }
        } catch (error) {
            console.error('Erro ao salvar:', error);
            toast.error("Erro ao conectar com o servidor");
        } finally {
            isLoading.value = false;
        }
    };

    return (
        <PageForm
            pageTitle="Confissão de Dívida"
            apiVersion=""
            isLoading={isLoading.value}
            breadcrumb={[
                { label: 'Contratos', url: OPERATIONS_LIST_URL },
                { label: 'Confissão de Dívida', url: '#' }
            ]}
            controlSlot={
                <div className="pt-4 flex justify-end">
                    <button
                        className="text-white bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => navigate(-1)}
                        disabled={isLoading.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleSubmit}
                        disabled={isLoading.value || isFormDisabled}
                    >
                        Salvar
                    </button>
                </div>
            }
        >
            <ReceivablesDownloader
                sequencialNumber={form.value.sequencial_number || 0}
                duplicatesCount={form.value.duplicates?.items?.filter(item => item.document_id || item.receivable_amount > 0).length || 0}
                onDataLoaded={handleJsonDataLoaded}
            />
            <div className="container mx-auto px-2 py-4">
                <h2 className="text-xl font-semibold mt-4 mb-4">Informações Gerais</h2>
                <div className="mb-4">
                    <BadgeRegistrationStatus
                        statusId={form.value.status === 'APPROVED' ? 4 : form.value.status === 'REJECTED' ? 5 : 1}
                        status={form.value.status || ''}
                    />
                </div>
                <div className="grid grid-cols-12 gap-4 mb-6">
                    <div className="col-span-2">
                        <TextEdit
                            label="Número do Contrato"
                            value={form.value.sequencial_number?.toString() || ''}
                            disabled={true}
                        />
                    </div>
                </div>

                <h2 className="text-xl font-semibold mt-4 mb-4">Dados Gerais</h2>
                <div className="grid grid-cols-10 gap-4 mb-6">
                    <div className="col-span-2">
                        <TextEdit
                            label="Operação quitada"
                            value={form.value.original_contract_discharged?.toString() || ''}
                            onChange={(e) => form.value = { ...form.value, original_contract_discharged: parseInt(e.target.value) || 0 }}
                            errorMessage={getErrorMessage(errorFields.value, "original_contract_discharged")}
                            required
                        />
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 mb-6">
                    <div className="col-span-3">
                        <TextEdit
                            label="Número da Operação"
                            value={form.value.operation_number}
                            onChange={(e) => form.value = { ...form.value, operation_number: e.target.value }}
                            errorMessage={getErrorMessage(errorFields.value, "operation_number")}
                        />
                    </div>
                    <div className="col-span-2">
                        <TextEdit
                            label="Card JIRA"
                            value={form.value.external_link}
                            onChange={(e) => form.value = { ...form.value, external_link: e.target.value }}
                            errorMessage={getErrorMessage(errorFields.value, "external_link")}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 mb-6">
                    <div className="col-span-3">
                        <TextEdit
                            label="CPF/CNPJ Originador"
                            value={form.value.originator}
                            onChange={(e) => form.value = { ...form.value, originator: e.target.value }}
                            errorMessage={getErrorMessage(errorFields.value, "originator")}
                        />
                    </div>
                    <div className="col-span-2">
                        <SelectTextField
                            label="UF da Operação"
                            value={form.value.operation_uf.toString()}
                            items={provinces}
                            onChange={(e) => handleProvinceChange(e.target.value)}
                            errorMessage={getErrorMessage(errorFields.value, "operation_uf")}
                        />
                    </div>
                    <div className="col-span-3">
                        <SelectTextField
                            label="Cidade da Operação"
                            value={form.value.operation_city}
                            items={cities}
                            onChange={(e) => form.value = { ...form.value, operation_city: e.target.value }}
                            errorMessage={getErrorMessage(errorFields.value, "operation_city")}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 mb-6">
                    <div className="col-span-4">
                        <TextEdit
                            label="Emitente"
                            value={form.value.assignor}
                            onChange={(e) => form.value = { ...form.value, assignor: e.target.value }}
                            errorMessage={getErrorMessage(errorFields.value, "assignor")}
                        />
                    </div>
                    <div className="col-span-4">
                        <DayPicker
                            label="Data de Emissão"
                            value={form.value.emission_date}
                            onChange={(value) => form.value = { ...form.value, emission_date: value }}
                            errorMessage={getErrorMessage(errorFields.value, "emission_date")}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 mb-6">
                    <div className="col-span-2">
                        <TextEdit
                            label="Buscar Beneficiário"
                            value={searchDestinatario}
                            onChange={(e) => handlePersonSearch(e.target.value)}
                            placeholder=""
                        />
                    </div>
                    <div className="col-span-6 flex items-end gap-2">
                        <div className="flex-1">
                            <TextEdit
                                label="Beneficiário"
                                value={form.value.recipient}
                                disabled={true}
                            />
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 mb-4"
                            title="Buscar beneficiário"
                        >
                            <FaSearch />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-10 gap-4 mb-6">
                    <div className="col-span-5">
                        <SelectTextField
                            label="Produto"
                            value={form.value.crop_type.id}
                            items={cropTypes}
                            onChange={(e) => {
                                const selected = cropTypes.find(crop => crop.value === e.target.value);
                                form.value = {
                                    ...form.value,
                                    crop_type: {
                                        id: e.target.value,
                                        name: selected?.label || ''
                                    }
                                };
                            }}
                            errorMessage={getErrorMessage(errorFields.value, "crop_type")}
                        />
                    </div>
                </div>

                <h2 className="text-xl font-semibold mt-4 mb-4">Desembolsos</h2>

                <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Valor do Desembolso"
                            prefix="R$"
                            fractionDigits={2}
                            value={newDisbursement.value.amount}
                            onChange={(value) => {
                                newDisbursement.value = { ...newDisbursement.value, amount: value };
                            }}
                        />
                    </div>
                    <div className="col-span-2">
                        <DayPicker
                            label="Data do Desembolso"
                            value={newDisbursement.value.date}
                            onChange={(value) => {
                                newDisbursement.value = { ...newDisbursement.value, date: value };
                            }}
                        />
                    </div>
                    <div className="col-span-2 flex items-end">
                        <button
                            onClick={addDisbursement}
                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isLoading.value || form.value.disbursement.disbursements.length >= 2}
                        >
                            Incluir
                        </button>
                    </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Valor</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Data</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                            {form.value.disbursement.disbursements.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                        Nenhum desembolso adicionado
                                    </td>
                                </tr>
                            ) : (
                                form.value.disbursement.disbursements.map((disbursement, index) => (
                                    <tr key={`${index}-${disbursement.amount}-${disbursement.date}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                            R$ {disbursement.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                            {disbursement.date}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    const disbursements = form.value.disbursement.disbursements.filter((_, i) => i !== index);
                                                    const totalAmount = disbursements.reduce((sum, d) => sum + d.amount, 0);
                                                    form.value = {
                                                        ...form.value,
                                                        disbursement: { disbursements, totalAmount }
                                                    };
                                                }}
                                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={isLoading.value}
                                            >
                                                Remover
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Total dos Desembolsos"
                            prefix="R$"
                            fractionDigits={2}
                            value={form.value.disbursement.totalAmount}
                            disabled={true}
                        />
                    </div>
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Valor de Abatimento"
                            prefix="R$"
                            fractionDigits={2}
                            value={form.value.rebate_amount}
                            onChange={(value) => form.value = { ...form.value, rebate_amount: value }}
                            errorMessage={getErrorMessage(errorFields.value, "rebate_amount")}
                        />
                    </div>
                </div>

                <h2 className="text-xl font-semibold mt-4 mb-4">Taxas e Encargos</h2>

                <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                    <div className="col-span-3">
                        <SelectTextField
                            label="Nome da Taxa"
                            value={newFee.value.name}
                            items={[
                                { value: 'Pré', label: 'Pré' },
                                { value: 'CDI', label: 'CDI' },
                                { value: 'CDI+', label: 'CDI+' },
                                { value: 'IPCA', label: 'IPCA' },
                                { value: 'IPCA+', label: 'IPCA+' },
                                { value: 'IGPM', label: 'IGPM' },
                                { value: 'IGPM+', label: 'IGPM+' }
                            ]}
                            onChange={(e) => {
                                newFee.value = { ...newFee.value, name: e.target.value };
                            }}
                        />
                    </div>
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Taxa (%)"
                            prefix="%"
                            fractionDigits={2}
                            value={newFee.value.rate}
                            onChange={(value) => {
                                newFee.value = { ...newFee.value, rate: value };
                            }}
                        />
                    </div>
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Valor (R$)"
                            prefix="R$"
                            fractionDigits={2}
                            value={newFee.value.amount}
                            onChange={(value) => {
                                newFee.value = { ...newFee.value, amount: value };
                            }}
                        />
                    </div>
                    <div className="col-span-3">
                        <SelectTextField
                            label="Forma"
                            value={newFee.value.form}
                            items={[
                                { value: 'PERCENTUAL', label: 'Percentual' },
                                { value: 'FIXO', label: 'Fixo' },
                                { value: 'ANUAL', label: 'Anual' }
                            ]}
                            onChange={(e) => {
                                newFee.value = { ...newFee.value, form: e.target.value };
                            }}
                        />
                    </div>
                    <div className="col-span-2 flex items-end">
                        <button
                            onClick={addFee}
                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isLoading.value}
                        >
                            Incluir
                        </button>
                    </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Nome</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Taxa</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Valor</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Forma</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                            {form.value.fees.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                        Nenhuma taxa adicionada
                                    </td>
                                </tr>
                            ) : (
                                form.value.fees.map((fee, index) => (
                                    <tr key={`${index}-${fee.name}-${fee.rate}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                            {fee.name}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                            {fee.rate.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                            R$ {fee.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                            {fee.form}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    const fees = form.value.fees.filter((_, i) => i !== index);
                                                    form.value = {
                                                        ...form.value,
                                                        fees
                                                    };
                                                }}
                                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={isLoading.value}
                                            >
                                                Remover
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <h2 className="text-xl font-semibold mt-4 mb-4">Parcelas</h2>

                <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                    <div className="col-span-2">
                        {newInstallment.value.format === 'PERCENTUAL' ? (
                            <CurrencyInput
                                label="Percentual da Parcela"
                                prefix="%"
                                fractionDigits={2}
                                value={newInstallment.value.value}
                                onChange={(value) => {
                                    const clampedValue = Math.min(Math.max(value, 0), 100);
                                    newInstallment.value = { ...newInstallment.value, value: clampedValue };
                                }}
                            />
                        ) : (
                            <CurrencyInput
                                label="Valor da Parcela"
                                prefix="R$"
                                fractionDigits={2}
                                value={newInstallment.value.value}
                                onChange={(value) => {
                                    newInstallment.value = { ...newInstallment.value, value: value };
                                }}
                            />
                        )}
                    </div>
                    <div className="col-span-2">
                        <DayPicker
                            label="Data de Vencimento"
                            value={newInstallment.value.dueDate}
                            onChange={(value) => {
                                newInstallment.value = { ...newInstallment.value, dueDate: value };
                            }}
                        />
                    </div>
                    <div className="col-span-2">
                        <SelectTextField
                            label="Formato"
                            value={newInstallment.value.format}
                            items={[
                                { value: 'VALOR', label: 'Valor' },
                                { value: 'PERCENTUAL', label: 'Percentual' }
                            ]}
                            onChange={(e) => {
                                newInstallment.value = { ...newInstallment.value, format: e.target.value };
                            }}
                        />
                    </div>
                    <div className="col-span-2 flex items-end">
                        <button
                            onClick={addInstallment}
                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isLoading.value}
                        >
                            Incluir
                        </button>
                    </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Parcela</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Valor</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Vencimento</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Formato</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                            {form.value.installments.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                        Nenhuma parcela adicionada
                                    </td>
                                </tr>
                            ) : (
                                form.value.installments
                                    .sort((a, b) => {
                                        const dateA = new Date(a.dueDate.split('/').reverse().join('-'));
                                        const dateB = new Date(b.dueDate.split('/').reverse().join('-'));
                                        return dateA.getTime() - dateB.getTime();
                                    })
                                    .map((installment, index) => (
                                        <tr key={`${index}-${installment.number}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                                {installment.number}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                                {installment.format === 'PERCENTUAL'
                                                    ? `${(installment.value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%`
                                                    : `R$ ${(installment.value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                }
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                                {installment.dueDate}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                                {installment.format || 'VALOR'}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <button
                                                    onClick={() => {
                                                        const originalIndex = form.value.installments.findIndex(inst => inst.number === installment.number);
                                                        const filteredInstallments = form.value.installments.filter((_, i) => i !== originalIndex);
                                                        const reprocessedInstallments = reprocessInstallmentNumbers(filteredInstallments);
                                                        form.value = {
                                                            ...form.value,
                                                            installments: reprocessedInstallments
                                                        };
                                                        updateTotalAndExpirationFromInstallments(reprocessedInstallments);
                                                    }}
                                                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={isLoading.value}
                                                >
                                                    Remover
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                            )}
                        </tbody>
                    </table>
                </div>

                <h2 className="text-xl font-semibold mt-4 mb-4">Vencimento</h2>
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-3">
                        <CurrencyInput
                            label="Valor Total"
                            prefix="R$"
                            fractionDigits={2}
                            value={form.value.total_amount}
                            onChange={(value) => form.value = { ...form.value, total_amount: value }}
                            errorMessage={getErrorMessage(errorFields.value, "total_amount")}
                            disabled={!form.value.installments.some(inst => inst.format === 'PERCENTUAL')}
                        />
                    </div>
                    <div className="col-span-3">
                        <DayPicker
                            label="Data de vencimento"
                            value={form.value.expiration_date}
                            onChange={(value) => form.value = { ...form.value, expiration_date: value }}
                            errorMessage={getErrorMessage(errorFields.value, "expiration_date")}
                            disabled={!form.value.installments.some(inst => inst.format === 'PERCENTUAL')}
                        />
                    </div>
                </div>

                <h2 className="text-xl font-semibold mt-4 mb-4">Avalistas</h2>

                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-3">
                        <CurrencyInput
                            label="% aval da operação"
                            id="guarantee_value"
                            prefix="%"
                            fractionDigits={2}
                            value={form.value.guarantee_value || 0}
                            onChange={(value) => form.value = { ...form.value, guarantee_value: value }}
                            errorMessage={getErrorMessage(errorFields.value, "guarantee_value")}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                    <div className="col-span-2">
                        <TextEdit
                            label="Buscar Avalista"
                            value={guarantorSearch}
                            onChange={(e) => setGuarantorSearch(e.target.value)}
                        />
                    </div>
                    <div className="col-span-8">
                        <SelectTextField
                            label="Avalista"
                            value={newGuarantor.value.uuid}
                            items={filteredGuarantors}
                            onChange={(e) => {
                                const selected = guarantors.find(g => g.value === e.target.value);
                                newGuarantor.value = {
                                    ...newGuarantor.value,
                                    uuid: e.target.value,
                                    name: selected?.label.split(' - ')[0] || '',
                                    entity_type: selected?.entity_type || ''
                                };
                            }}
                        />
                    </div>
                    <div className="col-span-2 flex items-end">
                        <button
                            onClick={addGuarantor}
                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isLoading.value}
                        >
                            Incluir
                        </button>
                    </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Nome</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Tipo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                            {form.value.guarantors.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                        Nenhum avalista adicionado
                                    </td>
                                </tr>
                            ) : (
                                form.value.guarantors.map((guarantor, index) => (
                                    <tr key={`${index}-${guarantor.uuid}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                            {guarantor.name}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${guarantor.entity_type === 'PJ' ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100' :
                                                'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                                }`}>
                                                {guarantor.entity_type === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    const guarantors = form.value.guarantors.filter((_, i) => i !== index);
                                                    form.value = {
                                                        ...form.value,
                                                        guarantors
                                                    };
                                                }}
                                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={isLoading.value}
                                            >
                                                Remover
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-6">
                        <SelectTextField
                            label="Risco Principal"
                            value={form.value.main_risk.uuid}
                            items={[
                                { value: assignorParticipant.uuid || '00000000-0000-0000-0000-000000000000', label: assignorParticipant.name || form.value.assignor || 'Emitente' },
                                ...form.value.guarantors.map(g => ({ value: g.uuid, label: g.name }))
                            ]}
                            onChange={(e) => {
                                if (e.target.value === 'assignor') {
                                    form.value = {
                                        ...form.value,
                                        main_risk: {
                                            uuid: '00000000-0000-0000-0000-000000000000',
                                            name: form.value.assignor || 'Emitente'
                                        }
                                    };
                                } else {
                                    const selected = form.value.guarantors.find(g => g.uuid === e.target.value);
                                    form.value = {
                                        ...form.value,
                                        main_risk: {
                                            uuid: e.target.value,
                                            name: selected?.name || ''
                                        }
                                    };
                                }
                            }}
                            errorMessage={getErrorMessage(errorFields.value, "main_risk")}
                        />
                    </div>
                </div>

                <h2 className="text-xl font-semibold mt-4 mb-4">Contrato</h2>
                <div>
                    <div className="flex items-center justify-center w-full">
                        <label htmlFor="contract-upload"
                            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                        >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">Upload do Contrato</span>
                                </p>
                            </div>
                            <input
                                id="contract-upload"
                                type="file"
                                className="hidden"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        contractFiles.value = [file];
                                    }
                                }}
                            />
                        </label>
                    </div>
                    {existingContractFile && !removedContractFile && (
                        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-800 border border-blue-200 dark:border-blue-600 rounded flex justify-between items-center">
                            <p className="text-sm text-blue-700 dark:text-blue-100">
                                <strong>Arquivo atual:</strong> {existingContractFile}
                            </p>
                            <button
                                onClick={() => setRemovedContractFile(true)}
                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                            >
                                <FaTimes className="inline mr-1" /> Remover
                            </button>
                        </div>
                    )}
                    {removedContractFile && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-800 border border-red-200 dark:border-red-600 rounded flex justify-between items-center">
                            <p className="text-sm text-red-700 dark:text-red-100">
                                <strong>Arquivo removido:</strong> {existingContractFile}
                            </p>
                            <button
                                onClick={() => setRemovedContractFile(false)}
                                className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                            >
                                Restaurar
                            </button>
                        </div>
                    )}
                    {contractFiles.value.length > 0 && (
                        <div className="mt-2 p-2 bg-green-50 dark:bg-green-800 border border-green-200 dark:border-green-600 rounded">
                            <p className="text-sm text-green-700 dark:text-green-100">
                                <strong>Novo arquivo:</strong> {contractFiles.value[0].name}
                            </p>
                        </div>
                    )}
                </div>

                <h2 className="text-xl font-semibold mt-4 mb-4">Artefatos</h2>
                {removedArtifactFiles.length > 0 && (
                    <div className="mt-2 p-3 bg-red-50 dark:bg-red-800 border border-red-200 dark:border-red-600 rounded">
                        <p className="text-sm font-medium text-red-700 dark:text-red-100 mb-2">Arquivos removidos:</p>
                        <ul className="text-sm text-red-600 dark:text-red-200 space-y-1">
                            {removedArtifactFiles.map((fileName, index) => (
                                <li key={index} className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                                        {fileName}
                                    </div>
                                    <button
                                        onClick={() => setRemovedArtifactFiles(removedArtifactFiles.filter(f => f !== fileName))}
                                        className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                    >
                                        Restaurar
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="mt-4">
                    <div className="flex items-center justify-center w-full">
                        <label htmlFor="artifacts-upload"
                            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                        >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">Clique aqui para subir arquivos</span> ou arraste e solte aqui.
                                </p>
                            </div>
                            <input
                                id="artifacts-upload"
                                type="file"
                                className="hidden"
                                multiple
                                onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    artifactFiles.value = [...artifactFiles.value, ...files];
                                }}
                            />
                        </label>
                    </div>
                </div>
                {existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)).length > 0 && (
                    <div className="mt-2 p-3 bg-green-50 dark:bg-green-800 border border-green-200 dark:border-green-600 rounded">
                        <p className="text-sm font-medium text-green-700 dark:text-green-100 mb-2">Arquivos atuais:</p>
                        <ul className="text-sm text-green-600 dark:text-green-200 space-y-1">
                            {existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)).map((fileName, index) => (
                                <li key={index} className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                        {fileName}
                                    </div>
                                    <button
                                        onClick={() => setRemovedArtifactFiles([...removedArtifactFiles, fileName])}
                                        className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                                    >
                                        <FaTimes className="inline" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {artifactFiles.value.length > 0 && (
                    <div className="mt-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                        <h3 className="text-sm font-medium mb-3 dark:text-gray-300">Arquivos Selecionados ({artifactFiles.value.length})</h3>
                        <div className="flex flex-wrap gap-4">
                            {artifactFiles.value.map((file, index) => {
                                const getFileIcon = (fileName: string) => {
                                    const ext = fileName.split('.').pop()?.toLowerCase();
                                    switch (ext) {
                                        case 'pdf': return <FaFilePdf className="text-red-500 text-2xl" />;
                                        case 'doc':
                                        case 'docx': return <FaFileWord className="text-blue-500 text-2xl" />;
                                        case 'xls':
                                        case 'xlsx': return <FaFileExcel className="text-green-500 text-2xl" />;
                                        case 'jpg':
                                        case 'jpeg':
                                        case 'png':
                                        case 'gif': return <FaFileImage className="text-purple-500 text-2xl" />;
                                        default: return <FaFile className="text-gray-500 text-2xl" />;
                                    }
                                };

                                return (
                                    <div key={index} className="relative flex flex-col items-center p-3 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded shadow-sm hover:shadow-md transition-shadow">
                                        <button
                                            onClick={() => artifactFiles.value = artifactFiles.value.filter((_, i) => i !== index)}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                                        >
                                            <FaTimes />
                                        </button>
                                        {getFileIcon(file.name)}
                                        <span className="text-xs mt-2 text-center max-w-20 truncate dark:text-gray-300" title={file.name}>
                                            {file.name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}



                <h2 className="text-xl font-semibold mt-4 mb-4">Duplicatas</h2>

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-300">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">#</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nome Sacado</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nome Cedente</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nº Título</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Valor Desembolso</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Data Emissão</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Data Vencimento</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Parcela</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">UF</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Cidade</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {form.value.duplicates?.items
                                ?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                .map((item, displayIndex) => {
                                    const actualIndex = (currentPage - 1) * itemsPerPage + displayIndex;
                                    return (
                                        <tr key={actualIndex} className="hover:bg-gray-50">
                                            <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-600">{actualIndex + 1}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.debtor_name}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.assignor_name}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.document_title}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.receivable_amount ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.receivable_amount) : 'R$ 0,00'}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.issue_date ? new Date(item.issue_date).toLocaleDateString('pt-BR') : ''}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.receivable_due_date ? new Date(item.receivable_due_date).toLocaleDateString('pt-BR') : ''}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.receivable_number}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.address_state}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.address_city}</td>
                                        </tr>
                                    );
                                }) || []}
                        </tbody>
                        <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            <tr>
                                <td colSpan={9} className="px-2 py-3 text-sm font-semibold text-gray-700">
                                    Total de registros: {form.value.duplicates?.items?.length || 0}
                                </td>
                                <td className="px-2 py-3 text-sm font-semibold text-gray-700 text-right">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                                        form.value.duplicates?.items?.reduce((sum, item) => sum + (item.receivable_amount || 0), 0) || 0
                                    )}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Paginação */}
                {(form.value.duplicates?.items?.length || 0) > itemsPerPage && (
                    <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                            Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, form.value.duplicates?.items?.length || 0)} de {form.value.duplicates?.items?.length || 0} duplicatas
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Anterior
                            </button>
                            <span className="px-3 py-1 text-sm">
                                Página {currentPage} de {Math.ceil((form.value.duplicates?.items?.length || 0) / itemsPerPage)}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil((form.value.duplicates?.items?.length || 0) / itemsPerPage)))}
                                disabled={currentPage === Math.ceil((form.value.duplicates?.items?.length || 0) / itemsPerPage)}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de busca de beneficiário */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-3/4 max-w-4xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Buscar Beneficiário</h3>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setModalSearchValue('');
                                    setModalSearchResults([]);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="mb-4">
                            <TextEdit
                                label="CPF/CNPJ ou Nome"
                                value={modalSearchValue}
                                onChange={(e) => handleModalSearch(e.target.value)}
                                placeholder=""
                            />
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full bg-white border border-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b h-12 w-auto">Documento</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b h-12 w-full">Nome</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b h-12 w-auto">Ação</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {Array.from({ length: 6 }, (_, index) => {
                                        const result = modalSearchResults[index];
                                        return (
                                            <tr key={index} className="hover:bg-gray-50 h-12">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm w-auto">
                                                    {result ? result.label.split(' - ')[0] : ''}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm w-full truncate">
                                                    {result ? result.label.split(' - ')[1] : ''}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm w-auto">
                                                    {result && (
                                                        <button
                                                            onClick={() => handleModalSelectParticipant(result)}
                                                            className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600"
                                                        >
                                                            Selecionar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </PageForm>
    );
};

export default AcknowledgementDebtPage;